import type { GameEvent, MatchState, Seat, Team } from "@guandan/engine";
import type { MatchSummary } from "./summary.js";

/**
 * The shape of a stored replay, and how to read one.
 *
 * This lives in the protocol package because both ends need it: the server
 * writes an archive when a match is archived, and the replay viewer in the
 * browser reads one back. The *writer* stays on the server — it needs the
 * stored event rows — but the schema and the decoder are shared, so a client
 * can never disagree with the server about what a tape means.
 */
export interface ReplayArchiveV2 {
  readonly schema: "guandan-replay-v2";
  readonly meta: {
    readonly roomId: string;
    readonly seed: string;
    readonly rulesetId: string;
    readonly coinTier: string;
    readonly entryCoins: number;
    /** Absent on archives written before the ranked/gold split; treat as "gold". */
    readonly mode?: string;
    readonly startedAt: number | null;
    readonly endedAt: number | null;
  };
  /** [seat, userId, team, isAi, ratingBefore] — declared once per match. */
  readonly players: readonly (readonly [Seat, string, Team, 0 | 1, number])[];
  /** [seq, deltaMs, eventCode, compactPayload] — one continuous multi-hand tape. */
  readonly events: readonly (readonly [number, number, string, unknown])[];
  /** Per canonical event [visualOffsetMs, visualHoldMs]; absent on old archives. */
  readonly visualTimings?: readonly (readonly [number, number])[];
  readonly summary: MatchSummary;
  readonly final: { readonly winner: Team | null; readonly levels: readonly number[]; readonly handNumber: number };
  /**
   * Present only on an archive sent to a viewer. `null` is the public spectator
   * view; a seat number means that seat's private cards are the only ones left.
   */
  readonly viewerSeat?: Seat | null;
}

export const eventCodes: Record<GameEvent["type"], string> = {
  handStarted: "h", dealt: "d", tributeResisted: "r", tributePaid: "t", tributeReturned: "u",
  turnChanged: "n", played: "p", passed: "v", trickEnded: "k", playerOut: "o",
  handEnded: "e", matchEnded: "m",
};

export function compactEvent(event: GameEvent): unknown {
  switch (event.type) {
    case "handStarted": return [event.handNumber, event.level, event.playingTeam];
    case "dealt": return event.hands;
    case "tributeResisted": return event.seats;
    case "tributePaid": return [event.from, event.to, event.card];
    case "tributeReturned": return [event.from, event.to, event.card];
    case "turnChanged": return [event.seat, event.open ? 1 : 0, event.phase];
    case "played": return [event.seat, event.cards, [event.combo.type, event.combo.rank, event.combo.length, event.combo.power], event.remaining];
    case "passed": return [event.seat];
    case "trickEnded": return [event.winner, event.leader, event.jiefeng ? 1 : 0];
    case "playerOut": return [event.seat, event.place];
    case "handEnded": return event.result;
    case "matchEnded": return [event.winner];
  }
}

export function decodeCompactEvent(code: string, payload: unknown): GameEvent {
  const data = Array.isArray(payload) ? payload : [];
  switch (code) {
    case "h": return { type: "handStarted", handNumber: data[0] as number, level: data[1] as never, playingTeam: data[2] as Team };
    case "d": return { type: "dealt", hands: data as number[][] };
    case "r": return { type: "tributeResisted", seats: data as Seat[] };
    case "t": return { type: "tributePaid", from: data[0] as Seat, to: data[1] as Seat, card: data[2] as number };
    case "u": return { type: "tributeReturned", from: data[0] as Seat, to: data[1] as Seat, card: data[2] as number };
    case "n": return { type: "turnChanged", seat: data[0] as Seat, open: data[1] === 1, phase: data[2] as "playing" | "returnTribute" };
    case "p": {
      const combo = Array.isArray(data[2]) ? data[2] : [];
      return { type: "played", seat: data[0] as Seat, cards: data[1] as number[], combo: { type: combo[0], rank: combo[1], length: combo[2], power: combo[3], cards: data[1] }, remaining: data[3] as number } as GameEvent;
    }
    case "v": return { type: "passed", seat: data[0] as Seat };
    case "k": return { type: "trickEnded", winner: data[0] as Seat, leader: data[1] as Seat, jiefeng: data[2] === 1 };
    case "o": return { type: "playerOut", seat: data[0] as Seat, place: data[1] as number };
    case "e": return { type: "handEnded", result: payload as never };
    case "m": return { type: "matchEnded", winner: data[0] as Team };
    default: throw new Error(`unknown-replay-event:${code}`);
  }
}

export function decodeReplayEvents(archive: ReplayArchiveV2): GameEvent[] {
  let previousSeq = 0;
  return archive.events.map(([seq, delta, code, payload]) => {
    if (!Number.isInteger(seq) || seq <= previousSeq || !Number.isFinite(delta) || delta < 0) throw new Error("invalid-replay-sequence");
    previousSeq = seq;
    return decodeCompactEvent(code, payload);
  });
}

/**
 * How long to wait before rendering each event, in tape order.
 *
 * The archive stores the gap between events rather than an absolute time, which
 * is exactly what the client scheduler wants as its `offset` — so a replay
 * paces itself the way the match actually played rather than at some invented
 * constant rate.
 */
export function replayOffsets(archive: ReplayArchiveV2): number[] {
  return archive.events.map(([, delta]) => delta);
}

export function parseReplayArchiveV2(bytes: Uint8Array): ReplayArchiveV2 {
  const archive = JSON.parse(new TextDecoder().decode(bytes)) as ReplayArchiveV2;
  if (archive.schema !== "guandan-replay-v2" || !Array.isArray(archive.events) || !archive.summary) {
    throw new Error("invalid-replay-v2");
  }
  return archive;
}

/**
 * Prepare an archive for an authorised replay viewer.
 *
 * Replay access is decided by the server before this runs: every participant
 * may inspect the complete finished deal, and sharing extends that same view to
 * anyone holding the public link. The shuffle seed is still unnecessary input
 * for the client and is removed; the canonical cards and roster are preserved.
 */
export function redactReplayArchive(archive: ReplayArchiveV2, viewerSeat: Seat | null): ReplayArchiveV2 {
  return {
    ...archive,
    viewerSeat,
    meta: { ...archive.meta, seed: "" },
  };
}

/** Validate a v2 event tape without mistaking projected events for intents. */
export function verifyReplayV2(archive: ReplayArchiveV2): { ok: true; events: readonly GameEvent[] } | { ok: false; reason: string } {
  try {
    const events = decodeReplayEvents(archive);
    if (events[0]?.type !== "handStarted" || events.at(-1)?.type !== "matchEnded") return { ok: false, reason: "missing-terminal-events" };
    const terminal = events.at(-1);
    if (terminal?.type !== "matchEnded" || terminal.winner !== archive.final.winner) return { ok: false, reason: "terminal-mismatch" };
    return { ok: true, events };
  } catch { return { ok: false, reason: "invalid-replay-v2" }; }
}

/** The state a replay ends in, for a caller that only wants the outcome. */
export type ReplayFinalState = Pick<MatchState, "winner" | "levels" | "handNumber">;
